/**
 * Hero imagery + mockup hooks.
 *
 * Generation is slow (~25s per concept) and costs real money per render, so candidates are a
 * MUTATION rather than a query: they must never fire on mount, refetch on focus, or retry on
 * error. Selection and catalog application invalidate the context so current-hero state stays
 * truthful without a manual refresh.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mockupApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'

export const mockupKeys = {
  all: ['mockups'] as const,
  context: (target?: string) => [...mockupKeys.all, 'context', target ?? null] as const,
  templates: () => [...mockupKeys.all, 'templates'] as const,
}

export function useMockupTargets() {
  return useQuery({
    queryKey: ['mockup-targets'],
    queryFn: () => mockupApi.getTargets(),
  })
}

export function useMockupContext(target?: string) {
  return useQuery({
    queryKey: mockupKeys.context(target),
    queryFn: () => mockupApi.getContext(target),
  })
}

export function useMockupTemplates() {
  return useQuery({
    queryKey: mockupKeys.templates(),
    queryFn: mockupApi.listTemplates,
  })
}

/**
 * Render concepts for one artwork. Nothing is persisted — the artist picks afterwards.
 * `retry: false` because a retry silently doubles the spend on a failed render.
 */
export function useGenerateCandidates() {
  const { toast } = useToast()
  return useMutation({
    mutationFn: mockupApi.generateCandidates,
    retry: false,
    onError: (e: Error) =>
      toast({ title: 'Could not generate concepts', description: e.message, variant: 'destructive' }),
  })
}

export function useSetHero(target?: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: mockupApi.setHero,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: mockupKeys.context(target) })
      toast({
        title: 'Hero updated',
        // Say what was preserved: the previous set is carried forward, not replaced, and that
        // is the detail people most expect to have gone wrong.
        description: `${data.carriedForward} existing mockup${data.carriedForward === 1 ? '' : 's'} kept behind it.${
          data.cacheCleared ? '' : ' Cache not cleared — it may take up to an hour to appear.'
        }`,
      })
    },
    onError: (e: Error) =>
      toast({ title: 'Could not set hero', description: e.message, variant: 'destructive' }),
  })
}

export function useApplyTemplate(target?: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: mockupApi.applyTemplate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: mockupKeys.context(target) })
      toast({
        title: `Applied to ${data.applied.length} artwork${data.applied.length === 1 ? '' : 's'}`,
        description: data.failed.length
          ? `${data.failed.length} failed — ${data.failed[0].name}: ${data.failed[0].error}`
          : 'Every piece now leads with this scene.',
        variant: data.failed.length ? 'destructive' : undefined,
      })
    },
    onError: (e: Error) =>
      toast({ title: 'Could not apply template', description: e.message, variant: 'destructive' }),
  })
}

// =============================================================================
// Scene directions
// =============================================================================

import { sceneApi } from '../api/easelApi'

export const sceneKeys = {
  all: ['scenes'] as const,
  templates: (target?: string) => [...sceneKeys.all, 'templates', target ?? null] as const,
}

export function useSceneTemplates(target?: string) {
  return useQuery({
    queryKey: sceneKeys.templates(target),
    queryFn: () => sceneApi.listScenes(target as string),
    enabled: !!target,
  })
}

/**
 * Proposing renders one image per direction, so it is slow and costs real money.
 * A mutation, never a query: it must not fire on mount, refetch on focus, or retry.
 */
export function useProposeDirections() {
  const { toast } = useToast()
  return useMutation({
    mutationFn: sceneApi.propose,
    retry: false,
    onError: (e: Error) =>
      toast({ title: 'Could not propose directions', description: e.message, variant: 'destructive' }),
  })
}

export function useBuildDirection(target?: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: sceneApi.build,
    retry: false,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: sceneKeys.templates(target) })
      toast({
        title: `Built ${data.built.length} scene${data.built.length === 1 ? '' : 's'}`,
        description: data.failed.length
          ? `${data.failed.length} could not be used: ${data.failed[0].reason}`
          : 'Apply them to your work when you are ready.',
        variant: data.failed.length ? 'destructive' : undefined,
      })
    },
    onError: (e: Error) =>
      toast({ title: 'Could not build the direction', description: e.message, variant: 'destructive' }),
  })
}

export function useApplyScenes(target?: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: sceneApi.apply,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: mockupKeys.context(target) })
      toast({
        title: `Updated ${data.applied.length} artwork${data.applied.length === 1 ? '' : 's'}`,
        // Say what was preserved — the hero is the thing people fear losing here.
        description: `Your hero and framed shot were kept on each piece.${
          data.failed.length ? ` ${data.failed.length} failed.` : ''
        }`,
        variant: data.failed.length ? 'destructive' : undefined,
      })
    },
    onError: (e: Error) =>
      toast({ title: 'Could not apply scenes', description: e.message, variant: 'destructive' }),
  })
}
